import { useCallback, useEffect } from "react";

const FIELD_SELECTOR = [
  "input:not([type='hidden']):not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[data-form-nav-field='true']:not([disabled])",
].join(", ");

function isVisibleElement(element) {
  if (!element) return false;
  if (element.hidden) return false;
  if (element.getAttribute("aria-hidden") === "true") return false;
  return element.getClientRects().length > 0;
}

function isNavigableField(element) {
  return (
    element &&
    !element.disabled &&
    element.tabIndex !== -1 &&
    isVisibleElement(element)
  );
}

function findNextField(form, currentElement) {
  const fields = Array.from(form.querySelectorAll(FIELD_SELECTOR)).filter(isNavigableField);
  const currentIndex = fields.indexOf(currentElement);

  if (currentIndex < 0) {
    return fields[0] || null;
  }

  return fields[currentIndex + 1] || null;
}

function focusSubmitOrSubmit(form) {
  const submitButton = form.querySelector("button[type='submit']:not([disabled]), input[type='submit']:not([disabled])");
  if (submitButton && isVisibleElement(submitButton)) {
    submitButton.focus();
    return;
  }
  form.requestSubmit();
}

export default function useFormKeyboardNavigation(formRef, refreshKey = "") {
  const focusFirstField = useCallback(() => {
    const form = formRef?.current;
    if (!form) return;
    const first = Array.from(form.querySelectorAll(FIELD_SELECTOR)).find(isNavigableField);
    first?.focus();
  }, [formRef]);

  const focusNextField = useCallback(
    (currentElement) => {
      const form = formRef?.current;
      if (!form || !currentElement) return;

      const next = findNextField(form, currentElement);
      if (next) {
        next.focus();
        return;
      }
      focusSubmitOrSubmit(form);
    },
    [formRef]
  );

  useEffect(() => {
    const form = formRef?.current;
    if (!form) return;

    const timer = window.setTimeout(focusFirstField, 0);

    const handleKeyDown = (event) => {
      if (event.key !== "Enter" || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.isComposing) return;

      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.matches(FIELD_SELECTOR)) return;

      if (target.dataset.formNavOpenOnly === "true") {
        return;
      }

      event.preventDefault();
      focusNextField(target);
    };

    form.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(timer);
      form.removeEventListener("keydown", handleKeyDown);
    };
  }, [formRef, focusFirstField, focusNextField, refreshKey]);

  return {
    focusFirstField,
    focusNextField,
  };
}
